import { useLocation } from "@tanstack/react-router";
import { Menu } from "lucide-react";

import { Link } from "@/components/link";
import { navIconMap } from "@/components/nav-icons";
import { useSidebar } from "@/components/ui/sidebar";
import { navItems } from "@/lib/dashboard-data";
import { cn } from "cn";

// Thumb-reach navigation for phones: the four pages a rep lives in, plus
// "More", which opens the full sidebar sheet. Hidden from md up (the sidebar
// takes over) and on short landscape screens, where every row of height counts
// and the header's page menu already covers navigation.
const PRIMARY = ["/", "/tasks", "/deals", "/customers"] as const;
const tabs = PRIMARY.map((href) => navItems.find((n) => n.href === href)!);

export function MobileTabBar() {
  const pathname = useLocation({ select: (s) => s.pathname });
  const { setOpenMobile, openMobile } = useSidebar();
  const active = (href: string) =>
    href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`);
  const onPrimary = tabs.some((t) => active(t.href));

  const item =
    "flex min-h-12 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium outline-none transition-colors focus-visible:bg-muted [&_svg]:size-5";

  return (
    <nav
      aria-label="Primary"
      className="flex shrink-0 border-t bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur supports-backdrop-filter:bg-background/80 md:hidden [@media(max-height:480px)]:hidden print:hidden"
    >
      {tabs.map((t) => {
        const Icon = navIconMap[t.icon];
        const on = active(t.href);
        return (
          <Link
            key={t.href}
            href={t.href}
            aria-current={on ? "page" : undefined}
            className={cn(item, on ? "text-foreground" : "text-muted-foreground")}
          >
            <Icon />
            {t.title === "Dashboard" ? "Home" : t.title}
          </Link>
        );
      })}
      <button
        type="button"
        aria-expanded={openMobile}
        onClick={() => setOpenMobile(true)}
        className={cn(item, !onPrimary ? "text-foreground" : "text-muted-foreground")}
      >
        <Menu />
        More
      </button>
    </nav>
  );
}
